import React, { useRef, useState } from 'react';
import { getPricingInfo } from '../../constants/pricing';

// Native replay state is session-local and opaque: never render, log, persist,
// or rebuild it from the visible answer. Buffered QC responses still use the
// native streaming provider and its final usage/cache breakdown underneath.
export default function KimiK31Playground({ models, onModelChange, model = 'kimi-k3.1-pro' }) {
  const title = model === 'kimi-k3.1-pro-highspeed' ? 'Kimi K3.1 Pro Highspeed' : 'Kimi K3.1 Pro';
  const rates = getPricingInfo(model);
  const history = useRef([]);
  const [messages, setMessages] = useState([]);
  const [prompt, setPrompt] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [effort, setEffort] = useState('max');
  const [budget, setBudget] = useState(131072);
  const [ttl, setTtl] = useState('5m');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function run() {
    if (!apiKey || !prompt.trim()) {
      setError('Enter an API key and a message.');
      return;
    }
    if (!Number.isInteger(budget) || budget < 1 || budget > 1048576) {
      setError('Completion budget must be an integer from 1 to 1048576.');
      return;
    }
    const user = { role: 'user', content: prompt };
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://qualitycompute.henosis.us'}/api/chat`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiKey}` },
        body: JSON.stringify({
          model, input: [user], stream: true,
          raw_provider_messages: [...history.current, user],
          max_completion_tokens: budget, reasoning_effort: effort,
          prompt_cache_options: { mode: 'implicit', ttl },
          include_builtin_tools: false,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Kimi request failed');
      const summary = data.response || data;
      const replay = summary.raw_provider_messages || summary.provider_state?.messages;
      if (!Array.isArray(replay) || replay[replay.length - 1]?.role !== 'assistant') {
        throw new Error('Complete native replay history is missing; start a new conversation before retrying.');
      }
      history.current = replay;
      const answer = summary.output_text || summary.selected_text || '';
      setMessages((previous) => [...previous, user, { role: 'assistant', content: answer }]);
      setPrompt('');
    } catch (err) {
      setError(err.message || 'Kimi request failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-6">
      <h2 className="text-xl font-bold">{title} — Native Moonshot</h2>
      <label htmlFor="kimi-model">Model</label>
      <select id="kimi-model" value={model} disabled={loading} onChange={(event) => onModelChange(event.target.value)}>
        {models.map((id) => <option key={id} value={id}>{id === 'kimi-k3.1-pro-highspeed' ? 'Kimi K3.1 Pro Highspeed' : id}</option>)}
      </select>
      <p>1,048,576-token context. Always thinking; fixed sampling parameters are omitted. This playground runs one native call per turn.</p>
      <label htmlFor="kimi-key">API Key (Required)</label>
      <input id="kimi-key" type="password" value={apiKey} onChange={(event) => setApiKey(event.target.value)} />
      <label htmlFor="kimi-effort">Kimi Reasoning</label>
      <select id="kimi-effort" value={effort} onChange={(event) => setEffort(event.target.value)}>
        <option value="low">Low</option><option value="high">High</option><option value="max">Max (default)</option>
      </select>
      <label htmlFor="kimi-budget">Max Completion Tokens (thinking + answer)</label>
      <input id="kimi-budget" type="number" min="1" max="1048576" value={budget} onChange={(event) => setBudget(Number(event.target.value))} />
      <label htmlFor="kimi-ttl">Cache TTL</label>
      <select id="kimi-ttl" value={ttl} onChange={(event) => setTtl(event.target.value)}>
        <option value="5m">5 minutes (default)</option><option value="1h">1 hour</option>
      </select>
      <p>Per million tokens: input ${rates.input}, cached input $0.20, output ${rates.output}; cache writes ${rates.cache_writes_5m} (5m) / ${rates.cache_writes_1h} (1h). Reads, writes, and uncached input are billed once as exclusive buckets. A cached prefix keeps its original TTL.</p>
      <p>Vision API: use base64 images or ms:// files, not public image URLs. Native web search is not production-ready and is disabled here.</p>
      <div aria-label="Kimi conversation">
        {messages.map((message, index) => <p key={index}><strong>{message.role}:</strong> {message.content}</p>)}
      </div>
      <label htmlFor="kimi-prompt">Message</label>
      <textarea id="kimi-prompt" value={prompt} onChange={(event) => setPrompt(event.target.value)} />
      {error && <p role="alert">{error}</p>}
      <button disabled={loading} onClick={run}>{loading ? 'Running...' : 'Run Conversation'}</button>
    </div>
  );
}
